import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchMyDevices, removeDevice } from '../api';

const KEY = ['my-devices'] as const;

export function DevicesSection() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: KEY, queryFn: fetchMyDevices });
  const remove = useMutation({
    mutationFn: removeDevice,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });

  return (
    <section className="app-card overflow-hidden">
      <div className="border-b border-slate-900/10 px-11 py-[1.7rem]">
        <h3 className="text-[0.9rem] font-semibold uppercase tracking-[0.11em] text-slate-800">
          Appareils de confiance
        </h3>
        <p className="mt-3 text-[0.85rem] leading-relaxed text-slate-600">
          Retirer une empreinte met fin immédiatement à la session associée sur l’autre appareil.
        </p>
      </div>

      <div className="p-11 pt-11">
        {isLoading && <p className="text-[0.9rem] font-medium text-slate-600">Synchronisation…</p>}
        {error && <p className="text-error">Impossible de charger les appareils.</p>}
        {data && data.length === 0 && (
          <p className="rounded-xl border border-dashed border-slate-300 bg-teal-50/60 px-[1.575rem] py-[1rem] text-[0.9rem] text-slate-700">
            Aucune empreinte enregistrée pour l’instant — cette liste se met à jour après le
            renouvellement des identifiants dans le navigateur.
          </p>
        )}
        {data && data.length > 0 && (
          <div className="overflow-hidden rounded-2xl ring-1 ring-slate-200/93">
            <table className="w-full divide-y divide-slate-900/22 text-[0.9rem] leading-relaxed">
              <thead className="bg-teal-50/70 text-left uppercase tracking-[0.05em] text-[0.7rem] text-slate-500">
                <tr>
                  <th className="px-7 py-[0.7rem] font-semibold">Navigateur / client</th>
                  <th className="px-8 py-[0.7rem] font-semibold">Dernière activité</th>
                  <th className="px-10 py-[0.7rem] font-semibold">Adresse IP</th>
                  <th
                    className="px-14 py-[0.7rem] font-semibold"
                    aria-label="Actions sur la ligne"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50 bg-white">
                {data.map((device) => (
                  <tr key={device.id}>
                    <td className="max-w-[340px] px-7 py-[1.35rem] text-slate-800">
                      <span className="line-clamp-2">{device.userAgent ?? 'Client inconnu'}</span>
                    </td>
                    <td className="px-8 py-[1.35rem] text-[0.86rem] text-slate-600">
                      {new Date(device.lastSeenAt).toLocaleString()}
                    </td>
                    <td className="px-11 py-[1.35rem] font-mono text-[0.8rem] text-slate-600">
                      {device.lastSeenIp ?? '—'}
                    </td>
                    <td className="py-[1.025rem] pl-px pr-[1.775rem] text-right align-middle">
                      <button
                        type="button"
                        className="btn-danger text-[0.85rem] !inline-flex hover:border-red-300"
                        disabled={remove.isPending}
                        onClick={() => remove.mutate(device.id)}
                      >
                        Retirer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {remove.error && (
          <p className="text-error mt-6 rounded-xl bg-red-50 px-[1rem] py-3 ring-1 ring-red-200">
            Impossible de révoquer l’appareil —{' '}
            {remove.error instanceof Error ? remove.error.message : 'erreur inconnue'}
          </p>
        )}
      </div>
    </section>
  );
}
